<template>
  <div class="timeline-page">
    <div class="timeline-header">
      <div class="header-left">
        <el-button type="text" icon="el-icon-arrow-left" @click="goBack">返回</el-button>
        <h2>{{ timelineTitle || (isEdit ? '编辑时间轴' : '新建时间轴') }}</h2>
      </div>
      <div class="header-right">
        <el-button type="primary" @click="saveTimeline" :loading="saving">
          {{ isEdit ? '更新' : '保存' }}
        </el-button>
      </div>
    </div>

    <div class="timeline-content">
      <!-- 时间轴主体 - 全屏显示 -->
      <div class="timeline-main">
        <TimelineComponent 
          :items="validTimelineItems" 
          :editable="true"
          @update-items="updateTimelineItems"
        />
      </div>

      <!-- 编辑侧边栏 -->
      <div class="timeline-sidebar" v-if="showEditor">
        <div class="sidebar-header">
          <h3>编辑时间轴</h3>
          <el-button size="mini" type="text" icon="el-icon-close" @click="showEditor = false"></el-button>
        </div>
        
        <el-form :model="timelineForm" label-width="60px" size="small">
          <el-form-item label="标题" style="font-size: 16px;font-weight: 600;color: #000;">
            <el-input 
              v-model="timelineForm.title" 
              placeholder="请输入时间轴标题" 
              maxlength="50" 
              show-word-limit
              @input="timelineTitle = timelineForm.title"
            />
          </el-form-item>
        </el-form>

        <div class="timeline-editor">
          <div class="editor-header">
            <h4>时间节点</h4>
            <el-button type="primary" size="mini" icon="el-icon-plus" @click="addTimelineItem">
              添加
            </el-button>
          </div>

          <div class="timeline-items" v-if="timelineItems.length > 0">
            <div 
              v-for="(item, index) in timelineItems" 
              :key="index" 
              class="timeline-item-editor"
            >
              <div class="item-header">
                <span>节点 {{ index + 1 }}</span>
                <div class="item-actions">
                  <el-button 
                    size="mini" 
                    type="text" 
                    icon="el-icon-top" 
                    :disabled="index === 0"
                    @click="moveItem(index, -1)"
                  >
                  </el-button>
                  <el-button 
                    size="mini" 
                    type="text" 
                    icon="el-icon-bottom"
                    :disabled="index === timelineItems.length - 1"
                    @click="moveItem(index, 1)"
                  >
                  </el-button>
                  <el-button 
                    size="mini" 
                    type="danger" 
                    icon="el-icon-delete"
                    @click="removeTimelineItem(index)"
                  >
                  </el-button>
                </div>
              </div>

              <el-form :model="item" label-width="60px" size="mini">
                <el-form-item label="年份">
                  <el-input-number 
                    v-model="item.year" 
                    :min="1900" 
                    :max="2100" 
                    size="mini"
                    style="width: 100%;"
                  />
                </el-form-item>
                <el-form-item label="位置">
                  <el-select v-model="item.position" placeholder="选择位置" size="mini" style="width: 100%;">
                    <el-option label="左侧" value="left"></el-option>
                    <el-option label="右侧" value="right"></el-option>
                  </el-select>
                </el-form-item>
                <el-form-item label="标题">
                  <el-input v-model="item.title" placeholder="请输入标题" size="mini" />
                </el-form-item>
                <el-form-item label="子标题">
                  <el-input v-model="item.subtitle" placeholder="请输入子标题（可选）" size="mini" />
                </el-form-item>
              </el-form>
            </div>
          </div>

          <div v-else class="empty-state">
            <i class="el-icon-time"></i>
            <p>点击添加按钮创建节点</p>
          </div>
        </div>
      </div>

      <!-- 浮动编辑按钮 -->
      <div class="floating-edit-btn" v-if="!showEditor" @click="showEditor = true">
        <i class="el-icon-edit"></i>
      </div>
    </div>
  </div>
</template>

<script>
import TimelineComponent from '@/components/timeline/TimelineComponent.vue'
import request from '@/http/request'

export default {
  name: 'TimelineEditor',
  components: {
    TimelineComponent
  },
  data() {
    return {
      timelineForm: {
        title: ''
      },
      timelineTitle: '',
      timelineItems: [],
      showEditor: false,
      saving: false,
      isEdit: false,
      timelineId: null
    }
  },
  computed: {
    validTimelineItems() {
      return this.timelineItems.filter(item => 
        item.year && item.title && item.title.trim() !== ''
      )
    }
  },
  methods: {
    goBack() {
      // 检查是否有历史记录可以返回
      // 如果是在新窗口中打开的（window.open），history.length 通常为 1，无法返回
      if (window.history.length > 1) {
        // 尝试返回上一页
        try {
          this.$router.go(-1)
        } catch (e) {
          // 如果返回失败，跳转到编辑器页面
          this.navigateToEditor()
        }
      } else {
        // 如果没有历史记录，跳转到编辑器页面
        this.navigateToEditor()
      }
    },
    
    navigateToEditor() {
      // 检查是否有 paperId 参数，返回到编辑器页面
      const paperId = this.$route.query.paperId
      if (paperId) {
        this.$router.push({
          path: '/editor/note',
          query: {
            paperId: paperId,
            t: Date.now()
          }
        })
      } else {
        // 如果没有 paperId，返回到编辑器首页
        this.$router.push('/editor/note')
      }
    },

    addTimelineItem() {
      // 自动左右交替
      const lastItem = this.timelineItems[this.timelineItems.length - 1]
      const newPosition = !lastItem ? 'left' : (lastItem.position === 'left' ? 'right' : 'left')
      
      const newItem = {
        year: new Date().getFullYear(),
        title: '',
        subtitle: '',
        position: newPosition
      }
      this.timelineItems.push(newItem)
    },

    removeTimelineItem(index) {
      this.$confirm('确定要删除这个时间节点吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.timelineItems.splice(index, 1)
        this.$message.success('删除成功')
      }).catch(() => {})
    },

    moveItem(index, direction) {
      const newIndex = index + direction
      if (newIndex >= 0 && newIndex < this.timelineItems.length) {
        const item = this.timelineItems.splice(index, 1)[0]
        this.timelineItems.splice(newIndex, 0, item)
      }
    },

    updateTimelineItems(newItems) {
      this.timelineItems = newItems
    },

    async saveTimeline() {
      // 验证表单
      if (!this.timelineForm.title.trim()) {
        this.$message.error('请输入时间轴标题')
        return
      }

      if (this.validTimelineItems.length === 0) {
        this.$message.error('请至少添加一个有效的时间节点')
        return
      }

      this.saving = true

      try {
        // 准备保存数据
        const saveData = {
          title: this.timelineForm.title.trim(),
          content: JSON.stringify({
            items: this.validTimelineItems,
            metadata: {
              createdAt: new Date().toISOString(),
              version: '1.0'
            }
          })
        }

        // 如果是编辑模式，添加ID
        if (this.isEdit && this.timelineId) {
          saveData.id = this.timelineId
        }

        const response = await request('/api/Timeline/saveTimeline', saveData)
        
        if (response && response.timeline_id) {
          this.$message.success(this.isEdit ? '时间轴更新成功' : '时间轴保存成功')
          
          // 如果是新建，记录ID用于后续编辑
          if (!this.isEdit) {
            this.timelineId = response.timeline_id
            this.isEdit = true
          }
          
          // 可以选择返回或继续编辑
          this.$confirm('保存成功！是否返回上一页？', '提示', {
            confirmButtonText: '返回',
            cancelButtonText: '继续编辑',
            type: 'success'
          }).then(() => {
            this.goBack()
          }).catch(() => {})
        }
      } catch (error) {
        console.error('保存时间轴失败:', error)
        this.$message.error(error.message || '保存失败，请重试')
      } finally {
        this.saving = false
      }
    },

    async loadTimelineData(timelineId) {
      try {
        // 调用时间轴列表接口，通过ID获取特定时间轴数据
        const response = await request('/api/Timeline/timelineList', {
          page: 1,
          limit: 100,
          keywords: '' // 如果有需要，可以通过其他方式过滤
        })

        if (response && response.length > 0) {
          // 从返回的列表中找到对应ID的时间轴
          const timelineData = response.find(item => item.timeline_id == timelineId)
          
          if (timelineData) {
            // 设置标题
            this.timelineTitle = timelineData.title || '未命名时间轴'
            this.timelineForm.title = this.timelineTitle
            
            // 解析content字段（JSON字符串）
            if (timelineData.content) {
              try {
                const contentData = JSON.parse(timelineData.content)
                if (contentData.items && Array.isArray(contentData.items)) {
                  this.timelineItems = contentData.items
                } else {
                  console.warn('时间轴内容格式异常:', contentData)
                  this.initDefaultTimeline()
                }
              } catch (parseError) {
                console.error('解析时间轴内容失败:', parseError)
                this.initDefaultTimeline()
              }
            } else {
              this.initDefaultTimeline()
            }
          } else {
            this.$message.error('未找到指定的时间轴数据')
            this.initDefaultTimeline()
          }
        } else {
          this.$message.error('获取时间轴数据失败')
          this.initDefaultTimeline()
        }
      } catch (error) {
        console.error('加载时间轴数据失败:', error)
        this.$message.error('加载失败，请重试')
        this.initDefaultTimeline()
      }
    },

    initDefaultTimeline() {
      // 添加默认的时间节点，左右交替
      const currentYear = new Date().getFullYear()
      this.timelineItems = [
        {
          year: currentYear - 2,
          title: '项目启动',
          subtitle: '初始阶段',
          position: 'left'
        },
        {
          year: currentYear - 1,
          title: '重要里程碑',
          subtitle: '中期进展',
          position: 'right'
        },
        {
          year: currentYear,
          title: '当前状态',
          subtitle: '现在',
          position: 'left'
        },
        {
          year: currentYear + 1,
          title: '未来计划',
          subtitle: '下一步',
          position: 'right'
        }
      ]
    }
  },

  mounted() {
    // 检查是否为编辑模式
    const timelineId = this.$route.query.id
    if (timelineId) {
      this.isEdit = true
      this.timelineId = timelineId
      // 加载现有时间轴数据
      this.loadTimelineData(timelineId)
    } else {
      // 新建模式，初始化默认数据
      this.initDefaultTimeline()
    }
  }
}
</script>

<style lang="scss" scoped>
.timeline-page {
  min-height: 100vh;
  background: #f5f7fa;
  display: flex;
  flex-direction: column;
  
  .timeline-header {
    background: white;
    padding: 16px 24px;
    border-bottom: 1px solid #e4e7ed;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
    
    .header-left {
      display: flex;
      align-items: center;
      gap: 16px;
      
      h2 {
        margin: 0;
        color: #303133;
        font-size: 18px;
      }
    }
    
    .header-right {
      display: flex;
      gap: 12px;
    }
  }
  
  .timeline-content {
    flex: 1;
    position: relative;
    display: flex;
    
    .timeline-main {
      flex: 1;
      height: calc(100vh - 80px);
      overflow: hidden;
    }
    
    .timeline-sidebar {
      width: 350px;
      background: white;
      border-left: 1px solid #e4e7ed;
      display: flex;
      flex-direction: column;
      height: calc(100vh - 80px);
      
      .sidebar-header {
        padding: 16px 20px;
        border-bottom: 1px solid #e4e7ed;
        display: flex;
        justify-content: space-between;
        align-items: center;
        
        h3 {
          margin: 0;
          color: #303133;
          font-size: 16px;
        }
      }
      
      .timeline-editor {
        flex: 1;
        padding: 16px 20px;
        overflow-y: auto;
      }
    }
    
    .floating-edit-btn {
      position: fixed;
      bottom: 30px;
      right: 30px;
      width: 56px;
      height: 56px;
      background: #409eff;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 12px rgba(64, 158, 255, 0.4);
      cursor: pointer;
      z-index: 1000;
      transition: all 0.3s ease;
      
      &:hover {
        background: #337ecc;
        transform: scale(1.1);
      }
      
      i {
        color: white;
        font-size: 20px;
      }
    }
  }
  
  .editor-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    
    h4 {
      margin: 0;
      color: #303133;
      font-size: 14px;
    }
  }
  
  .timeline-items {
    max-height: 70vh;
    overflow-y: auto;
  }
  
  .timeline-item-editor {
    border: 1px solid #e4e7ed;
    border-radius: 6px;
    padding: 12px;
    margin-bottom: 12px;
    background: #fafafa;
    
    .item-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      
      span {
        font-weight: 600;
        color: #303133;
        font-size: 12px;
      }
      
      .item-actions {
        display: flex;
        gap: 4px;
      }
    }
  }
  
  .empty-state {
    text-align: center;
    padding: 40px 20px;
    color: #909399;
    
    i {
      font-size: 32px;
      margin-bottom: 12px;
      display: block;
    }
    
    p {
      margin: 0;
      font-size: 12px;
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .timeline-content {
    .timeline-sidebar {
      width: 100%;
      position: fixed;
      top: 80px;
      right: 0;
      z-index: 1001;
      height: calc(100vh - 80px);
    }
    
    .floating-edit-btn {
      bottom: 20px;
      right: 20px;
      width: 48px;
      height: 48px;
      
      i {
        font-size: 18px;
      }
    }
  }
}
</style>
